import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Coins, Eye, Send } from "lucide-react";
import { api, errMsg, imgSrc } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader, TierGate } from "@/components/Guards";
import { TipDialog } from "@/components/MoneyDialogs";

function Stage({ room, burst }) {
  return (
    <div className="relative aspect-video overflow-hidden bg-black" data-testid="cam-stage">
      <img src={imgSrc(room.host?.photo)} alt="" className="h-full w-full scale-110 object-cover opacity-80 blur-[1px]" />
      <div className="scanline pointer-events-none absolute inset-x-0 h-1/3 bg-gradient-to-b from-transparent via-white/5 to-transparent" />
      <span className="absolute left-4 top-4 flex items-center gap-1.5 rounded bg-red-600 px-2 py-0.5 text-xs font-bold uppercase tracking-widest"><span className="live-dot h-2 w-2 rounded-full bg-white" />Live</span>
      <span data-testid="cam-viewers" className="absolute right-4 top-4 flex items-center gap-1 rounded bg-black/60 px-2 py-1 text-xs"><Eye className="h-3 w-3" />{room.viewers}</span>
      <p className="absolute bottom-4 left-4 rounded bg-black/60 px-3 py-1 text-xs text-zinc-300">Video stream preview · live streaming coming soon</p>
      {burst && <div key={burst.k} className="tip-burst font-display absolute inset-x-0 bottom-1/3 text-center text-5xl text-amber-300">+{burst.n} tokens</div>}
    </div>
  );
}

function Chat({ roomId, refreshKey }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const end = useRef(null);
  const load = () => api.get(`/cams/${roomId}/chat`).then((r) => setMsgs(r.data));
  useEffect(() => {
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, refreshKey]);
  useEffect(() => { end.current?.scrollIntoView({ block: "nearest" }); }, [msgs.length]);
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try { await api.post(`/cams/${roomId}/chat`, { text }); setText(""); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <aside className="card-lux flex h-[480px] flex-col lg:h-auto" data-testid="cam-chat">
      <p className="eyebrow border-b border-white/5 p-4">Live chat</p>
      <div className="flex-1 space-y-2 overflow-y-auto p-4 text-sm">
        {msgs.length === 0 && <p className="text-zinc-600">Say hello to the host.</p>}
        {msgs.map((m) => (
          <p key={m.id} className={m.kind === "tip" ? "rounded bg-amber-500/10 px-2 py-1 text-amber-300" : ""}>
            <span className="font-semibold text-red-400">{m.name}</span> {m.text}
          </p>
        ))}
        <div ref={end} />
      </div>
      <form onSubmit={send} className="flex gap-2 border-t border-white/5 p-3">
        <input data-testid="cam-chat-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Chat…" className="flex-1 rounded-full bg-zinc-900 px-4 py-2 text-sm outline-none" />
        <button data-testid="cam-chat-send-btn" className="btn-red rounded-full px-4"><Send className="h-4 w-4" /></button>
      </form>
    </aside>
  );
}

export default function CamRoom() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [room, setRoom] = useState(null);
  const [burst, setBurst] = useState(null);
  const [k, setK] = useState(0);
  useEffect(() => {
    const load = () => api.get(`/cams/${id}`).then((r) => setRoom(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [id]);
  const onTipped = (n) => { setBurst({ n, k: Date.now() }); setK((x) => x + 1); };
  const end = async () => { await api.post(`/cams/${id}/end`); toast.success("Show ended"); nav("/cams"); };
  return (
    <main className="mx-auto max-w-7xl px-5 py-10 lg:px-8" data-testid="cam-room-page">
      <TierGate tier="vip" title="Live cams are for VIPs">
        {!room ? <Loader /> : (
          <div className="grid gap-5 lg:grid-cols-[1fr_360px]">
            <div>
              <Stage room={room} burst={burst} />
              <div className="mt-5 flex flex-wrap items-center justify-between gap-4">
                <div><h1 className="font-display text-4xl">{room.host?.name}</h1><p className="text-sm text-zinc-400">{room.title}{!room.is_live && " · offline"}</p></div>
                {room.host_id === user.id ? (
                  <button data-testid="cam-end-btn" onClick={end} className="rounded-full border border-white/20 px-6 py-3 text-sm hover:bg-white hover:text-black">End show</button>
                ) : room.host && (
                  <TipDialog to={room.host} roomId={room.id} onTipped={onTipped}
                    trigger={<button data-testid="cam-tip-btn" className="btn-red flex items-center gap-2 rounded-full px-8 py-3 font-semibold"><Coins className="h-4 w-4" />Tip tokens</button>} />
                )}
              </div>
            </div>
            <Chat roomId={room.id} refreshKey={k} />
          </div>
        )}
      </TierGate>
    </main>
  );
}
